/**
 * useReorganizeModules - Business logic hook for drag-and-drop module reorganization
 */

import { DndContextProps, DragEndEvent, DragStartEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { AddItemState, CourseInfo, EditItemState, EvaluationPeriod, ModuleItemType, SubmoduleItem } from 'app/types/modules';
import { createNewModule, createNewSubmodule, deleteLesson, editLesson, editUnit, putCourseItem, reorderItems } from 'core/crud/lessons.crud';
import { useOrganization } from 'core/hooks/useOrganization';
import { useTranslate } from 'core/hooks/useTranslate';
import { useCourse } from 'core/lite/hooks/useCourse';
import { useCourseItems } from 'core/lite/hooks/useCourseItems';
import { useUser } from 'core/lite/hooks/useUser';
import { EVALUATION_PERIODS, EVALUATION_PERIODS_TYPES } from 'core/utils/constants/periods';
import { CLIENT } from 'core/variables/constant';
import { useCallback, useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';

type UseDialogOrganizeCourseProps = {
  courseGuid: string;
  programGuid: string;
  onClose: () => void;
};

type UseDialogOrganizeCourseReturn = {
  periods: EvaluationPeriod[];
  activeId: string | null;
  activeDragType: 'module' | 'submodule' | null;
  activePeriodId: string | null;
  activeParentModuleGuid: string | null;
  isSaving: boolean;
  sensors: ReturnType<typeof useSensors>;
  expandedPeriods: Record<string, boolean>;
  expandedModules: Record<string, boolean>;
  moduleIds: string[];
  activeModule: ModuleItemType | null;
  activeSubmodule: SubmoduleItem | null;
  courseName: string;
  themeColor: string;
  gradient: string;
  themeImage: string;
  coursePicture: string;
  classroomName: string | null;
  structureLevels: 1 | 2;
  isDataLoading: boolean;
  isEdelvives: boolean;
  reorderMode: boolean;
  hasPendingChanges: boolean;
  setReorderMode: (mode: boolean) => void;
  handleCloseDialog: () => void;
  handleDragStart: DndContextProps['onDragStart'];
  handleDragEnd: DndContextProps['onDragEnd'];
  handleDragOver: (event: { over: { id: string | number } | null }) => void;
  handleDragCancel: DndContextProps['onDragCancel'];
  handleSaveChanges: () => void;
  handleCancelChanges: () => void;
  togglePeriodExpansion: (periodGuid: string) => void;
  toggleModuleExpansion: (moduleGuid: string) => void;
  // Add item state and handlers
  addItemState: AddItemState;
  handleOpenAddModule: (periodId: string) => void;
  handleOpenAddSubmodule: (parentModuleGuid: string) => void;
  handleCloseAdd: () => void;
  // Edit item state and handlers
  editItemState: {
    isOpen: boolean;
    item: ModuleItemType | SubmoduleItem | null;
    type: 'module' | 'submodule' | null;
  };
  handleOpenEdit: (item: ModuleItemType | SubmoduleItem) => void;
  handleCloseEdit: () => void;
  // Delete item state and handlers
  deleteItemState: {
    isOpen: boolean;
    item: ModuleItemType | SubmoduleItem | null;
    type: 'module' | 'submodule' | null;
  };
  handleOpenDelete: (item: ModuleItemType | SubmoduleItem) => void;
  handleCloseDelete: () => void;
  // Toggle visibility state and handlers
  toggleVisibilityState: {
    isOpen: boolean;
    item: ModuleItemType | SubmoduleItem | null;
    type: 'module' | 'submodule' | null;
  };
  handleOpenToggleVisibility: (item: ModuleItemType | SubmoduleItem) => void;
  handleCloseToggleVisibility: () => void;
  handleConfirmToggleVisibility: () => void;
  // Photo upload state and handlers
  photoState: {
    isOpen: boolean;
    item: ModuleItemType | SubmoduleItem | null;
    type: 'module' | 'submodule' | null;
  };
  handleOpenPhoto: (item: ModuleItemType | SubmoduleItem) => void;
  handleClosePhoto: () => void;
  handlePhotoRefresh: () => void;
  // Confirmation handlers
  handleConfirmAdd: (name: string) => Promise<void>;
  handleConfirmEdit: (name: string) => Promise<void>;
  handleConfirmDelete: () => Promise<void>;
  isCreating: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  isTogglingVisibility: boolean;
  // Unsaved changes dialog state and handlers
  showUnsavedChangesDialog: boolean;
  handleDiscardAndClose: () => void;
  handleSaveAndClose: () => Promise<void>;
  handleCancelUnsavedChanges: () => void;
};

function useDialogOrganizeCourse({ courseGuid, programGuid, onClose }: UseDialogOrganizeCourseProps): UseDialogOrganizeCourseReturn {
  const { t } = useTranslate();
  const { addToast } = useToasts();
  const { user } = useUser();
  const { client } = useOrganization();
  const isEdelvives = client === CLIENT.EDELVIVES;
  const { courseItems, loading: courseItemsLoading, refetch: refetchCourseItems } = useCourseItems(courseGuid);
  const { data: course, loading: courseLoading } = useCourse(courseGuid) as unknown as { data: CourseInfo; loading: boolean };

  // Track if this is the initial load or a refetch
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Set hasLoadedOnce to true after the first successful load
  useEffect(() => {
    if (!courseItemsLoading && !courseLoading && courseItems.length > 0 && !hasLoadedOnce) {
      setHasLoadedOnce(true);
    }
  }, [courseItemsLoading, courseLoading, courseItems.length, hasLoadedOnce]);

  // Build periods from courseItems and evaluationPeriodType
  const buildPeriodsFromCourseItems = useCallback((): EvaluationPeriod[] => {
    const evaluationPeriodType = course?.evaluation_period ?? EVALUATION_PERIODS.ANUAL;
    const periodConfig = EVALUATION_PERIODS_TYPES.find((config) => config.name === evaluationPeriodType);
    if (courseItemsLoading || courseLoading) {
      return [];
    }
    if (!periodConfig) {
      console.error(`Unknown evaluation period type: ${evaluationPeriodType}`);
      return [];
    }

    // Create period structure with empty units
    const periodsMap: Record<string, EvaluationPeriod> = {};
    periodConfig.periods.forEach((periodDef) => {
      periodsMap[periodDef.id] = {
        id: periodDef.id,
        name: periodDef.name,
        units: [],
      };
    });

    // Group courseItems by evaluation_period_id
    courseItems.forEach((item) => {
      const periodId = item.evaluation_period_id;
      if (periodId && periodsMap[periodId]) {
        periodsMap[periodId].units.push({ ...item });
      } else {
        // If no valid period_id, add to first period as fallback
        const firstPeriodId = periodConfig.periods[0]?.id;
        if (firstPeriodId && periodsMap[firstPeriodId]) {
          periodsMap[firstPeriodId].units.push({ ...item });
        }
      }
    });

    // Convert map to array and sort units by order
    return periodConfig.periods.map((periodDef) => {
      const period = periodsMap[periodDef.id];
      period.units.sort((a, b) => a.order - b.order);
      return period;
    });
  }, [courseItems, courseItemsLoading, courseLoading, course]);

  const [reorderMode, setReorderMode] = useState(true);
  const [periods, setPeriods] = useState<EvaluationPeriod[]>(() => buildPeriodsFromCourseItems());
  const [originalPeriods, setOriginalPeriods] = useState<EvaluationPeriod[] | null>(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeDragType, setActiveDragType] = useState<'module' | 'submodule' | null>(null);
  const [activePeriodId, setActivePeriodId] = useState<string | null>(null);
  const [activeParentModuleGuid, setActiveParentModuleGuid] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Add item dialog state
  const [addItemState, setAddItemState] = useState<AddItemState>({
    isOpen: false,
    type: null,
    periodId: null,
    parentModuleGuid: null,
  });

  // Edit item dialog state
  const [editItemState, setEditItemState] = useState<EditItemState>({
    isOpen: false,
    item: null,
    type: null,
  });

  // Delete item dialog state
  const [deleteItemState, setDeleteItemState] = useState<EditItemState>({
    isOpen: false,
    item: null,
    type: null,
  });

  // Toggle visibility dialog state
  const [toggleVisibilityState, setToggleVisibilityState] = useState<EditItemState>({
    isOpen: false,
    item: null,
    type: null,
  });

  // Photo upload dialog state
  const [photoState, setPhotoState] = useState<EditItemState>({
    isOpen: false,
    item: null,
    type: null,
  });

  // Unsaved changes confirmation dialog state
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  // Initialize all periods as expanded
  const [expandedPeriods, setExpandedPeriods] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    const periodConfig = EVALUATION_PERIODS_TYPES.find((config) => config.name === (course?.evaluation_period ?? EVALUATION_PERIODS.ANUAL));
    if (periodConfig) {
      periodConfig.periods.forEach((periodDef) => {
        initial[periodDef.id] = true;
      });
    }
    return initial;
  });

  // Initialize all modules as collapsed (closed by default)
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    courseItems.forEach((item) => {
      initial[item.guid] = false;
    });
    return initial;
  });

  useEffect(() => {
    setReorderMode(false);
  }, []);

  // Rebuild periods when courseItems or evaluationPeriodType changes
  useEffect(() => {
    setPeriods(buildPeriodsFromCourseItems());
  }, [buildPeriodsFromCourseItems]);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Helper: Determine if the dragged item is a module or submodule
  function getDragType(id: string, currentPeriods: EvaluationPeriod[]): 'module' | 'submodule' | null {
    for (const period of currentPeriods) {
      const isModule = period.units.some((unit) => unit.guid === id);
      if (isModule) return 'module';

      if (course?.structure_levels === 2) {
        for (const unit of period.units) {
          const isSubmodule = unit.items?.some((item) => item.guid === id);
          if (isSubmodule) return 'submodule';
        }
      }
    }
    return null;
  }

  // Helper: Find the period ID that contains the given module or submodule
  function findPeriodIdForItem(itemId: string, currentPeriods: EvaluationPeriod[]): string | null {
    for (const period of currentPeriods) {
      // Check if it's a module in this period
      const isModule = period.units.some((unit) => unit.guid === itemId);
      if (isModule) return period.id;

      // Check if it's a submodule in any module of this period
      if (course?.structure_levels === 2) {
        for (const unit of period.units) {
          const isSubmodule = unit.items?.some((item) => item.guid === itemId);
          if (isSubmodule) return period.id;
        }
      }
    }
    return null;
  }

  // Helper: Find the parent module GUID for a submodule
  function findParentModuleGuid(submoduleId: string, currentPeriods: EvaluationPeriod[]): string | null {
    for (const period of currentPeriods) {
      for (const unit of period.units) {
        const isSubmodule = unit.items?.some((item) => item.guid === submoduleId);
        if (isSubmodule) return unit.guid;
      }
    }
    return null;
  }

  // Helper: Find module position in periods
  function findModulePosition(moduleGuid: string, currentPeriods: EvaluationPeriod[]): { periodIndex: number; moduleIndex: number } | null {
    for (let pIndex = 0; pIndex < currentPeriods.length; pIndex++) {
      const mIndex = currentPeriods[pIndex].units.findIndex((u) => u.guid === moduleGuid);
      if (mIndex !== -1) {
        return { periodIndex: pIndex, moduleIndex: mIndex };
      }
    }
    return null;
  }

  // Helper: Build payload for API - only items that changed
  function buildReorderPayload(
    newPeriods: EvaluationPeriod[],
    originalPeriods: EvaluationPeriod[] | null
  ): Array<{ guid: string; order: number; evaluation_period_id?: string }> {
    if (!originalPeriods) {
      return [];
    }

    const changedItems: Array<{ guid: string; order: number; evaluation_period_id?: string }> = [];

    // Create a map of original items for quick lookup
    const originalItemsMap = new Map<string, { order: number; evaluation_period_id?: string; parent_guid?: string }>();

    originalPeriods.forEach((period) => {
      period.units.forEach((unit) => {
        originalItemsMap.set(unit.guid, {
          order: unit.order,
          evaluation_period_id: period.id,
        });

        if (course?.structure_levels === 2 && unit.items) {
          unit.items.forEach((submodule) => {
            originalItemsMap.set(submodule.guid, {
              order: submodule.order,
              parent_guid: unit.guid,
            });
          });
        }
      });
    });

    // Check new periods for changes
    newPeriods.forEach((period) => {
      period.units.forEach((unit, unitIndex) => {
        const original = originalItemsMap.get(unit.guid);
        const newOrder = unitIndex + 1; // Order starts from 1

        // Check if module changed order or period
        if (original && (original.order !== newOrder || original.evaluation_period_id !== period.id)) {
          changedItems.push({
            guid: unit.guid,
            order: newOrder,
            evaluation_period_id: period.id,
          });
        }

        if (course?.structure_levels === 2 && unit.items) {
          unit.items.forEach((submodule, subIndex) => {
            const originalSub = originalItemsMap.get(submodule.guid);
            const newSubOrder = subIndex + 1; // Order starts from 1

            // Check if submodule changed order or parent
            if (originalSub && (originalSub.order !== newSubOrder || originalSub.parent_guid !== unit.guid)) {
              changedItems.push({
                guid: submodule.guid,
                order: newSubOrder,
              });
            }
          });
        }
      });
    });

    return changedItems;
  }

  // Helper: Save reorder to the API
  async function saveReorder(newPeriods: EvaluationPeriod[]) {
    setIsSaving(true);

    const items = buildReorderPayload(newPeriods, originalPeriods);

    // If no items changed, just clear the pending state
    if (items.length === 0) {
      setHasPendingChanges(false);
      setOriginalPeriods(null);
      setIsSaving(false);
      addToast(t('organize-content:No changes to save'), {
        appearance: 'info',
        autoDismiss: true,
      });
      return;
    }

    try {
      await reorderItems(programGuid, {
        course_guid: courseGuid,
        course_items: items,
      });

      addToast(t('organize-content:Reordered correctly'), {
        appearance: 'success',
        autoDismiss: true,
      });

      // Clear pending changes state after successful save
      setHasPendingChanges(false);
      setOriginalPeriods(null);
      if (showUnsavedChangesDialog) {
        onClose();
      }

      // Refetch to ensure we have the latest data
      refetchCourseItems();
    } catch {
      // Rollback to original state on error
      if (originalPeriods) {
        setPeriods(originalPeriods);
      }

      addToast(t('organize-content:Could not reorder'), {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsSaving(false);
    }
  }

  // Handler: Save pending changes
  function handleSaveChanges() {
    if (hasPendingChanges) {
      saveReorder(periods);
    }
  }

  // Handler: Cancel pending changes
  function handleCancelChanges() {
    if (originalPeriods) {
      setPeriods(originalPeriods);
      setOriginalPeriods(null);
      setHasPendingChanges(false);

      addToast(t('organize-content:Changes discarded'), {
        appearance: 'info',
        autoDismiss: true,
      });
    }
  }

  // Helper: Reorder module within same period or move between periods
  function handleModuleReorder(activeIdStr: string, overIdStr: string, currentPeriods: EvaluationPeriod[]): EvaluationPeriod[] | null {
    const sourcePos = findModulePosition(activeIdStr, currentPeriods);
    if (!sourcePos) return null;

    // Find target position
    let targetPos = findModulePosition(overIdStr, currentPeriods);

    // Check if target is a period (dropping on period header)
    if (!targetPos) {
      const pIndex = currentPeriods.findIndex((p) => p.id === overIdStr);
      if (pIndex !== -1) {
        targetPos = {
          periodIndex: pIndex,
          moduleIndex: currentPeriods[pIndex].units.length,
        };
      } else {
        return null;
      }
    }

    const newPeriods = [...currentPeriods];

    if (sourcePos.periodIndex === targetPos.periodIndex) {
      // Same period - just reorder
      newPeriods[sourcePos.periodIndex] = {
        ...newPeriods[sourcePos.periodIndex],
        units: arrayMove(newPeriods[sourcePos.periodIndex].units, sourcePos.moduleIndex, targetPos.moduleIndex),
      };
    } else {
      // Different periods - move module
      const [movedModule] = newPeriods[sourcePos.periodIndex].units.splice(sourcePos.moduleIndex, 1);
      movedModule.evaluation_period_id = newPeriods[targetPos.periodIndex].id;
      newPeriods[targetPos.periodIndex].units.splice(targetPos.moduleIndex, 0, movedModule);
    }

    // Update order indices
    newPeriods.forEach((period) => {
      period.units.forEach((unit, idx) => {
        unit.order = idx;
      });
    });

    return newPeriods;
  }

  // Helper: Reorder submodule within same parent module
  function handleSubmoduleReorder(activeIdStr: string, overIdStr: string, currentPeriods: EvaluationPeriod[]): EvaluationPeriod[] | null {
    let sourceModuleGuid: string | null = null;
    let sourceSubmoduleIndex = -1;
    let targetSubmoduleIndex = -1;

    // Find source submodule
    for (const period of currentPeriods) {
      for (const unit of period.units) {
        const sIndex = unit.items?.findIndex((item) => item.guid === activeIdStr) ?? -1;
        if (sIndex !== -1) {
          sourceModuleGuid = unit.guid;
          sourceSubmoduleIndex = sIndex;
          break;
        }
      }
      if (sourceModuleGuid) break;
    }

    if (!sourceModuleGuid) return null;

    // Find target submodule (must be in same module)
    for (const period of currentPeriods) {
      for (const unit of period.units) {
        if (unit.guid === sourceModuleGuid) {
          const tIndex = unit.items?.findIndex((item) => item.guid === overIdStr) ?? -1;
          if (tIndex !== -1) {
            targetSubmoduleIndex = tIndex;
            break;
          }
        }
      }
      if (targetSubmoduleIndex !== -1) break;
    }

    if (sourceSubmoduleIndex === -1 || targetSubmoduleIndex === -1) return null;

    const newPeriods = currentPeriods.map((period) => ({
      ...period,
      units: period.units.map((unit) => {
        if (unit.guid === sourceModuleGuid && unit.items) {
          const newItems = arrayMove(unit.items, sourceSubmoduleIndex, targetSubmoduleIndex);
          // Update order indices
          newItems.forEach((item, idx) => {
            item.order = idx;
          });
          return { ...unit, items: newItems };
        }
        return unit;
      }),
    }));

    return newPeriods;
  }

  // Event handlers
  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    const id = active.id as string;
    const dragType = getDragType(id, periods);

    setActiveId(id);
    setActiveDragType(dragType);
    setActivePeriodId(findPeriodIdForItem(id, periods));

    // If dragging a submodule, find its parent module
    if (dragType === 'submodule') {
      setActiveParentModuleGuid(findParentModuleGuid(id, periods));
    } else {
      setActiveParentModuleGuid(null);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    setActiveId(null);
    setActiveDragType(null);
    setActivePeriodId(null);
    setActiveParentModuleGuid(null);

    if (!over || active.id === over.id) {
      return;
    }

    const activeIdStr = active.id as string;
    const overIdStr = over.id as string;
    const dragType = getDragType(activeIdStr, periods);

    // Store original state on first change (deep copy)
    if (!hasPendingChanges) {
      setOriginalPeriods(JSON.parse(JSON.stringify(periods)));
      setHasPendingChanges(true);
    }

    let newPeriods: EvaluationPeriod[] | null = null;

    if (dragType === 'module') {
      newPeriods = handleModuleReorder(activeIdStr, overIdStr, periods);
    } else if (dragType === 'submodule' && course?.structure_levels === 2) {
      newPeriods = handleSubmoduleReorder(activeIdStr, overIdStr, periods);
    }

    if (newPeriods) {
      setPeriods(newPeriods);
    }
  }

  function handleDragOver(event: { over: { id: string | number } | null }) {
    const { over } = event;
    if (!over) return;

    const overId = over.id as string;

    // Check if we're dragging a module over a period
    const dragType = activeDragType;
    if (dragType === 'module') {
      // Check if overId is a period ID
      const isPeriod = periods.some((period) => period.id === overId);
      if (isPeriod && !expandedPeriods[overId]) {
        // Auto-expand the period when dragging over it
        setExpandedPeriods((prev) => ({
          ...prev,
          [overId]: true,
        }));
      }
    }
  }

  function handleDragCancel() {
    setActiveId(null);
    setActiveDragType(null);
    setActivePeriodId(null);
    setActiveParentModuleGuid(null);
  }

  function togglePeriodExpansion(periodGuid: string) {
    setExpandedPeriods((prev) => ({
      ...prev,
      [periodGuid]: !prev[periodGuid],
    }));
  }

  function toggleModuleExpansion(moduleGuid: string) {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleGuid]: !prev[moduleGuid],
    }));
  }

  function handleCloseDialog() {
    // If there are pending changes, show confirmation dialog
    if (hasPendingChanges) {
      setShowUnsavedChangesDialog(true);
      return;
    }

    // Otherwise, close normally
    closeDialogWithoutSaving();
  }

  function closeDialogWithoutSaving() {
    setActiveId(null);
    setActiveDragType(null);
    setActivePeriodId(null);
    setActiveParentModuleGuid(null);
    setOriginalPeriods(null);
    setHasPendingChanges(false);
    setPeriods(buildPeriodsFromCourseItems());
    setExpandedPeriods({});
    setExpandedModules({});
    setShowUnsavedChangesDialog(false);

    // Refetch course items to update StandardView
    refetchCourseItems();

    onClose();
  }

  function handleDiscardAndClose() {
    closeDialogWithoutSaving();
  }

  async function handleSaveAndClose() {
    if (hasPendingChanges) {
      await saveReorder(periods);
      // After successful save, close the dialog
      if (!hasPendingChanges) {
        closeDialogWithoutSaving();
      }
    }
  }

  function handleCancelUnsavedChanges() {
    setShowUnsavedChangesDialog(false);
  }

  // Calculate derived values
  const moduleIds = periods.flatMap((period) => period.units.map((unit) => unit.guid));

  const activeModule =
    activeId && activeDragType === 'module'
      ? (() => {
          for (const period of periods) {
            const module = period.units.find((u) => u.guid === activeId);
            if (module) return module;
          }
          return null;
        })()
      : null;

  const activeSubmodule =
    activeId && activeDragType === 'submodule'
      ? (() => {
          for (const period of periods) {
            for (const unit of period.units) {
              const submodule = unit.items?.find((item) => item.guid === activeId);
              if (submodule) return submodule;
            }
          }
          return null;
        })()
      : null;

  // Add item handlers
  const handleOpenAddModule = useCallback((periodId: string) => {
    setAddItemState({
      isOpen: true,
      type: 'module',
      periodId,
      parentModuleGuid: null,
    });
  }, []);

  const handleOpenAddSubmodule = useCallback((parentModuleGuid: string) => {
    setAddItemState({
      isOpen: true,
      type: 'submodule',
      periodId: null,
      parentModuleGuid,
    });
  }, []);

  const handleCloseAdd = useCallback(() => {
    setAddItemState({
      isOpen: false,
      type: null,
      periodId: null,
      parentModuleGuid: null,
    });
  }, []);

  // Edit item handlers
  const handleOpenEdit = useCallback((item: ModuleItemType | SubmoduleItem) => {
    const isModule = 'section' in item;
    setEditItemState({
      isOpen: true,
      item,
      type: isModule ? 'module' : 'submodule',
    });
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditItemState({
      isOpen: false,
      item: null,
      type: null,
    });
  }, []);

  // Delete item handlers
  const handleOpenDelete = useCallback((item: ModuleItemType | SubmoduleItem) => {
    const isModule = 'section' in item;
    setDeleteItemState({
      isOpen: true,
      item,
      type: isModule ? 'module' : 'submodule',
    });
  }, []);

  const handleCloseDelete = useCallback(() => {
    setDeleteItemState({
      isOpen: false,
      item: null,
      type: null,
    });
  }, []);

  // Toggle visibility handlers
  const handleOpenToggleVisibility = useCallback((item: ModuleItemType | SubmoduleItem) => {
    const isModule = 'section' in item;
    setToggleVisibilityState({
      isOpen: true,
      item,
      type: isModule ? 'module' : 'submodule',
    });
  }, []);

  const handleCloseToggleVisibility = useCallback(() => {
    setToggleVisibilityState({
      isOpen: false,
      item: null,
      type: null,
    });
  }, []);

  const handleConfirmToggleVisibility = useCallback(async () => {
    if (!toggleVisibilityState.item) {
      console.error('No item to toggle visibility');
      return;
    }

    const item = toggleVisibilityState.item;
    const itemType = toggleVisibilityState.type;
    const currentVisibility = item.visibility ?? 1;
    const newVisibility = currentVisibility === 0 ? 1 : 0;

    // Get name based on item type
    const isModule = itemType === 'module';
    const itemName = isModule ? (item as ModuleItemType).section || (item as ModuleItemType).lesson_name || '' : (item as SubmoduleItem).lesson_name;

    setIsTogglingVisibility(true);

    try {
      // Call the toggle visibility endpoint
      // For modules: use "section" field
      // For submodules: use "lesson_name" field
      const payload: { visibility: boolean; course_guid: string; section?: string; lesson_name?: string } = {
        visibility: newVisibility === 1 ? true : false,
        course_guid: courseGuid,
      };

      if (isModule) {
        payload.section = itemName;
      } else {
        payload.lesson_name = itemName;
      }

      await putCourseItem(programGuid, item.guid, payload);

      const actionKey = newVisibility === 1 ? 'show' : 'hide';
      addToast(t(itemType === 'module' ? `organize-content:Module ${actionKey} ok` : `organize-content:Submodule ${actionKey} ok`), {
        appearance: 'success',
        autoDismiss: true,
      });

      // Refetch to update the dialog with the new visibility
      // The loading screen won't show because hasLoadedOnce prevents it
      refetchCourseItems();

      // Close the toggle visibility dialog
      handleCloseToggleVisibility();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      const actionKey = newVisibility === 1 ? 'show' : 'hide';
      addToast(t(itemType === 'module' ? `organize-content:Module ${actionKey} error` : `organize-content:Submodule ${actionKey} error`), {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsTogglingVisibility(false);
    }
  }, [toggleVisibilityState, programGuid, courseGuid, addToast, t, refetchCourseItems, handleCloseToggleVisibility]);

  // Photo upload handlers
  const handleOpenPhoto = useCallback((item: ModuleItemType | SubmoduleItem) => {
    const isModule = 'section' in item;
    setPhotoState({
      isOpen: true,
      item,
      type: isModule ? 'module' : 'submodule',
    });
  }, []);

  const handleClosePhoto = useCallback(() => {
    setPhotoState({
      isOpen: false,
      item: null,
      type: null,
    });
  }, []);

  const handlePhotoRefresh = useCallback(() => {
    // Refetch course items to show the new image
    refetchCourseItems();
  }, [refetchCourseItems]);

  // Module/Submodule creation logic
  const [isCreating, setIsCreating] = useState(false);

  // Module/Submodule edit logic
  const [isEditing, setIsEditing] = useState(false);

  // Module/Submodule deletion logic
  const [isDeleting, setIsDeleting] = useState(false);

  // Module/Submodule visibility toggle logic
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);

  const getNextModuleOrder = useCallback(
    (periodId: string): number => {
      const period = periods.find((p) => p.id === periodId);
      if (!period || period.units.length === 0) return 0;
      return Math.max(...period.units.map((u) => u.order)) + 1;
    },
    [periods]
  );

  const getNextSubmoduleOrder = useCallback(
    (parentModuleGuid: string): number => {
      for (const period of periods) {
        const module = period.units.find((u) => u.guid === parentModuleGuid);
        if (module) {
          if (!module.items || module.items.length === 0) return 0;
          return Math.max(...module.items.map((i) => i.order)) + 1;
        }
      }
      return 0;
    },
    [periods]
  );

  const createModule = useCallback(
    async (name: string, periodId: string): Promise<boolean> => {
      setIsCreating(true);
      try {
        // Create module with visibility: 0 and status: 'published'
        const response = await createNewModule(programGuid, {
          is_accessible: 1,
          section: name,
          name: name,
          visibility: 0,
          status: 'published',
          evaluation_period_id: periodId,
          order: getNextModuleOrder(periodId),
          is_customized: 1,
          created_by: user.guid,
        });

        // After successful creation, update visibility to 1
        if (response?.data?.data?.guid) {
          await putCourseItem(programGuid, response.data.data.guid, {
            visibility: true,
            course_guid: courseGuid,
            section: name,
          });
        }

        addToast(t('organize-content:Module create ok'), {
          appearance: 'success',
          autoDismiss: true,
        });

        // Refetch to show the new module immediately in the dialog
        // The loading screen won't show because hasLoadedOnce prevents it
        refetchCourseItems();

        return true;
      } catch (error) {
        console.error('Error creating module:', error);
        addToast(t('organize-content:Module create error'), {
          appearance: 'error',
          autoDismiss: true,
        });
        return false;
      } finally {
        setIsCreating(false);
      }
    },
    [programGuid, courseGuid, getNextModuleOrder, addToast, t, user.guid, refetchCourseItems]
  );

  const createSubmodule = useCallback(
    async (name: string, parentModuleGuidOrPeriodId: string, isOneLevelCourse: boolean = false): Promise<boolean> => {
      setIsCreating(true);
      try {
        let parentEvaluationPeriodId: string | null = null;
        let parentGuid: string | null = null;
        let order: number = 0;

        if (isOneLevelCourse) {
          // For 1-level courses: parentModuleGuidOrPeriodId is actually the periodId
          parentEvaluationPeriodId = parentModuleGuidOrPeriodId;
          parentGuid = null; // No parent module in 1-level courses
          order = getNextModuleOrder(parentModuleGuidOrPeriodId); // Use module order for 1-level courses
        } else {
          // For 2-level courses: find the parent module to get its evaluation_period_id
          for (const period of periods) {
            const parentModule = period.units.find((u) => u.guid === parentModuleGuidOrPeriodId);
            if (parentModule) {
              parentEvaluationPeriodId = parentModule.evaluation_period_id;
              parentGuid = parentModuleGuidOrPeriodId;
              break;
            }
          }
          order = getNextSubmoduleOrder(parentModuleGuidOrPeriodId);
        }

        // Create submodule with visibility: 0 and status: 'published'
        const response = await createNewSubmodule(programGuid, {
          evaluation_period_id: parentEvaluationPeriodId || '',
          is_active: 1,
          name: name,
          number_of_sessions: 1,
          parent_guid: parentGuid,
          type: 'teacher',
          visibility: 0,
          status: 'published',
          order: order,
          is_customized: 1,
          is_accessible: 1,
          created_by: user.guid,
        });
        // After successful creation, update visibility to 1
        if (response?.data?.data?.guid) {
          await putCourseItem(programGuid, response.data.data.guid, {
            visibility: true,
            course_guid: courseGuid,
            lesson_name: name,
          });
        }

        addToast(t('organize-content:Submodule create ok'), {
          appearance: 'success',
          autoDismiss: true,
        });

        // Refetch to show the new submodule immediately in the dialog
        // The loading screen won't show because hasLoadedOnce prevents it
        refetchCourseItems();

        return true;
      } catch (error) {
        console.error('Error creating submodule:', error);
        addToast(t('organize-content:Submodule create error'), {
          appearance: 'error',
          autoDismiss: true,
        });
        return false;
      } finally {
        setIsCreating(false);
      }
    },
    [programGuid, courseGuid, periods, getNextModuleOrder, getNextSubmoduleOrder, addToast, t, user.guid, refetchCourseItems]
  );

  // Confirmation handlers
  const handleConfirmAdd = useCallback(
    async (name: string) => {
      if (addItemState.type === 'module' && addItemState.periodId) {
        // Logic for creating a module:
        // - 2-level course: Create a module (item) using createModule
        // - 1-level course: Create a submodule (lesson) without parent using createSubmodule
        if (course?.structure_levels === 2) {
          const success = await createModule(name, addItemState.periodId);
          if (success) {
            handleCloseAdd();
          }
        } else {
          // 1-level course: modules are actually lessons without parent
          const success = await createSubmodule(name, addItemState.periodId, true);
          if (success) {
            handleCloseAdd();
          }
        }
      } else if (addItemState.type === 'submodule' && addItemState.parentModuleGuid) {
        const success = await createSubmodule(name, addItemState.parentModuleGuid, false);
        if (success) {
          handleCloseAdd();
        }
      }
    },
    [addItemState, course?.structure_levels, createModule, createSubmodule, handleCloseAdd]
  );

  const handleConfirmEdit = useCallback(
    async (name: string) => {
      if (!editItemState.item || !name.trim()) {
        console.error('No item to edit or invalid name');
        return;
      }

      const item = editItemState.item;
      const itemType = editItemState.type;
      const isModule = itemType === 'module';

      setIsEditing(true);

      try {
        // Determine which endpoint to use based on course structure and item type:
        // - Module in 2-level course → editUnit
        // - Module in 1-level course → editLesson
        // - Submodule → editLesson
        if (isModule && course?.structure_levels === 2) {
          // Module in 2-level course: use editUnit endpoint
          await editUnit({
            course_guid: programGuid,
            guid: item.guid,
            data: {
              section: name.trim(),
              is_customized: 1,
            },
          });
        } else {
          // Module in 1-level course OR Submodule: use editLesson endpoint
          await editLesson({
            course_guid: programGuid,
            guid: item.guid,
            data: {
              name: name.trim(),
              is_customized: 1,
            },
          });
        }

        addToast(t(itemType === 'module' ? 'organize-content:Module edit ok' : 'organize-content:Submodule edit ok'), {
          appearance: 'success',
          autoDismiss: true,
        });

        // Refetch to update the dialog with the new name
        // The loading screen won't show because hasLoadedOnce prevents it
        refetchCourseItems();

        // Close the edit dialog
        handleCloseEdit();
      } catch (error) {
        console.error('Error editing item:', error);
        addToast(t(itemType === 'module' ? 'organize-content:Module edit error' : 'organize-content:Submodule edit error'), {
          appearance: 'error',
          autoDismiss: true,
        });
      } finally {
        setIsEditing(false);
      }
    },
    [editItemState, course?.structure_levels, courseGuid, addToast, t, refetchCourseItems, handleCloseEdit]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteItemState.item) {
      console.error('No item to delete');
      return;
    }

    const itemGuid = deleteItemState.item.guid;
    const itemType = deleteItemState.type;

    setIsDeleting(true);

    try {
      // Call the delete endpoint according to TAN-4428 specs
      await deleteLesson({
        course_guid: programGuid,
        guid: itemGuid,
      });

      addToast(t(itemType === 'module' ? 'organize-content:Module delete ok' : 'organize-content:Submodule delete ok'), {
        appearance: 'success',
        autoDismiss: true,
      });

      // Refetch to update the dialog with the deleted item removed
      // The loading screen won't show because hasLoadedOnce prevents it
      refetchCourseItems();

      // Close the delete dialog
      handleCloseDelete();
    } catch (error) {
      console.error('Error deleting item:', error);
      addToast(t(itemType === 'module' ? 'organize-content:Module delete error' : 'organize-content:Submodule delete error'), {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteItemState, programGuid, addToast, t, refetchCourseItems, handleCloseDelete]);

  return {
    reorderMode,
    setReorderMode,
    periods,
    activeId,
    activeDragType,
    activePeriodId,
    activeParentModuleGuid,
    isSaving,
    hasPendingChanges,
    sensors,
    expandedPeriods,
    expandedModules,
    moduleIds,
    activeModule,
    activeSubmodule,
    courseName: course?.name ?? '',
    classroomName: course?.school_group_name ?? null,
    structureLevels: course?.structure_levels ?? 1,
    isDataLoading: (courseItemsLoading || courseLoading) && !hasLoadedOnce,
    isEdelvives,
    themeColor: course?.theme_color ?? '',
    gradient: course?.gradient ?? '',
    themeImage: course?.theme_image ?? '',
    coursePicture: course?.picture ? course.picture : course?.theme_image,
    handleCloseDialog,
    handleSaveChanges,
    handleCancelChanges,
    togglePeriodExpansion,
    toggleModuleExpansion,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragCancel,
    addItemState,
    handleOpenAddModule,
    handleOpenAddSubmodule,
    handleCloseAdd,
    editItemState,
    handleOpenEdit,
    handleCloseEdit,
    deleteItemState,
    handleOpenDelete,
    handleCloseDelete,
    toggleVisibilityState,
    handleOpenToggleVisibility,
    handleCloseToggleVisibility,
    handleConfirmToggleVisibility,
    photoState,
    handleOpenPhoto,
    handleClosePhoto,
    handlePhotoRefresh,
    handleConfirmAdd,
    handleConfirmEdit,
    handleConfirmDelete,
    isCreating,
    isEditing,
    isDeleting,
    isTogglingVisibility,
    showUnsavedChangesDialog,
    handleDiscardAndClose,
    handleSaveAndClose,
    handleCancelUnsavedChanges,
  };
}

export default useDialogOrganizeCourse;
